import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

const STORAGE_KEY = 'rin-theme';
const FONT_SIZE_KEY = 'rin-font-size';
const LIGHT_META = '#EEF2FF';
const DARK_META = '#0F0F1A';

const FONT_SIZE_MAP = {
  small: '14px',
  default: '16px',
  large: '18px',
  'x-large': '20px',
};

function getSystemDark() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolve(pref) {
  if (pref === 'dark') return 'dark';
  if (pref === 'light') return 'light';
  return getSystemDark() ? 'dark' : 'light';
}

function applyTheme(resolved) {
  const root = document.documentElement;
  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  const meta = document.getElementById('theme-color-meta');
  if (meta) meta.setAttribute('content', resolved === 'dark' ? DARK_META : LIGHT_META);
}

function applyFontSize(size) {
  document.documentElement.style.fontSize = FONT_SIZE_MAP[size] || FONT_SIZE_MAP.default;
}

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || 'system';
  });
  const [resolvedTheme, setResolvedTheme] = useState(() => resolve(preference));
  const [fontSize, setFontSizeState] = useState(() => {
    return localStorage.getItem(FONT_SIZE_KEY) || 'default';
  });

  const setTheme = useCallback((newPref) => {
    localStorage.setItem(STORAGE_KEY, newPref);
    setPreferenceState(newPref);
    const resolved = resolve(newPref);
    setResolvedTheme(resolved);

    // Smooth transition
    document.documentElement.classList.add('theme-transition');
    applyTheme(resolved);
    setTimeout(() => {
      document.documentElement.classList.remove('theme-transition');
    }, 350);
  }, []);

  const setFontSize = useCallback((size) => {
    localStorage.setItem(FONT_SIZE_KEY, size);
    setFontSizeState(size);
    applyFontSize(size);
  }, []);

  // Listen for OS theme changes when preference is "system"
  useEffect(() => {
    if (preference !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      const resolved = resolve('system');
      setResolvedTheme(resolved);
      applyTheme(resolved);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [preference]);

  // Apply on mount (handles SSR mismatch edge case)
  useEffect(() => {
    applyTheme(resolve(preference));
    applyFontSize(fontSize);
  }, []);

  return (
    <ThemeContext.Provider value={{ preference, resolvedTheme, setTheme, fontSize, setFontSize }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
